import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetDispatcherListQuery, useUpdateDispatcherStatusMutation, useDeleteDispatcherMutation } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";

const ManageDispatcher = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const status = 2; // As per API requirement

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Fetch dispatchers from API
  const { data, isLoading, error, refetch } = useGetDispatcherListQuery({
    page,
    limit,
    search: debouncedSearchTerm,
    status,
    isDropdown: false,
  });

  // Update dispatcher status mutation
  const [updateDispatcherStatus, { isLoading: isUpdating }] = useUpdateDispatcherStatusMutation();
  
  // Delete dispatcher mutation
  const [deleteDispatcher, { isLoading: isDeleting }] = useDeleteDispatcherMutation();

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [dispatcherToDelete, setDispatcherToDelete] = useState<{ id: string; name: string } | null>(null);

  // Extract dispatchers from API response
  const dispatchers = useMemo(() => {
    let dispatcherList: any[] = [];
    
    // Handle different response structures
    if (data?.data) {
      if (Array.isArray(data.data)) {
        // Direct array response
        dispatcherList = data.data;
      } else if (data.data.list && Array.isArray(data.data.list)) {
        // Nested list response
        dispatcherList = data.data.list;
      }
    }
    
    return dispatcherList.map((dispatcher: any) => ({
      id: dispatcher._id || dispatcher.id,
      name: dispatcher.fullName || `${dispatcher.firstName || ""} ${dispatcher.lastName || ""}`.trim() || "N/A",
      email: dispatcher.email || "N/A",
      phone: dispatcher.phone ? `${dispatcher.countryCode || ""} ${dispatcher.phone}`.trim() : "N/A",
      address: dispatcher.address?.addressLine1 || dispatcher.address?.fullAddress || "N/A",
      image: dispatcher.image || "/static/images/user_placeholder.png",
      isBlocked: dispatcher.isBlocked || false,
      rawData: dispatcher, // Store raw data for edit/view
    }));
  }, [data]);

  // Calculate total pages - handle different response structures
  const totalCount = useMemo(() => {
    if (data?.totalCount) return data.totalCount;
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && data.data.totalCount) {
      return data.data.totalCount;
    }
    // Fallback to array length if no pagination info
    return dispatchers.length;
  }, [data, dispatchers.length]);
  
  const totalPages = Math.ceil(totalCount / limit) || 1;

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setDispatcherToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the dispatcher
  const handleConfirmDelete = async () => {
    if (!dispatcherToDelete) return;

    try {
      const response = await deleteDispatcher({ id: dispatcherToDelete.id }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || `Dispatcher "${dispatcherToDelete.name}" deleted successfully`;
      showToast(successMessage);
      
      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setDispatcherToDelete(null);
    } catch (error: any) {
      console.error("Error deleting dispatcher:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete dispatcher. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setDispatcherToDelete(null);
    }
  };

  // Handle toggle change (enable/disable dispatcher)
  const handleToggleChange = async (dispatcherId: string, currentIsBlocked: boolean, dispatcherName: string) => {
    try {
      const newIsBlocked = !currentIsBlocked;
      
      const response = await updateDispatcherStatus({
        id: dispatcherId,
        isBlocked: newIsBlocked,
      }).unwrap();
      
      const successMessage = response?.message || 
        `Dispatcher "${dispatcherName}" has been ${newIsBlocked ? "disabled" : "enabled"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to update the UI
      refetch();
    } catch (error: any) {
      console.error("Error updating dispatcher status:", error);
      
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update dispatcher status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state
      refetch();
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchTerm("");
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdating} />
      <div className="main_title">
        <h1>Manage Dispatchers</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Dispatchers
        </p>
      </div>

      <div className="cards">
        {/* Header with Search and Add Button */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
              onCross={handleSearchClear}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search Dispatchers"
            />
          </div>

          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => navigate("add")}
            >
              <AddIcon /> Add Dispatcher
            </Button>
          </div>
        </div>

        {/* Table */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dispatcher Details</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Enable / Disable</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <div style={{ padding: "20px", color: "#d32f2f" }}>
                      {error && 'data' in error && typeof error.data === 'object' && error.data !== null && 'message' in error.data
                        ? (error.data as any).message
                        : "Error loading dispatchers. Please try again."}
                    </div>
                  </TableCell>
                </TableRow>
              ) : dispatchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {isLoading ? "Loading..." : "No dispatchers found"}
                  </TableCell>
                </TableRow>
              ) : (
                dispatchers.map((dispatcher) => (
                  <TableRow hover key={dispatcher.id}>
                    {/* Dispatcher Details */}
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img
                            src={dispatcher.image}
                            alt="Dispatcher"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/static/images/user_placeholder.png";
                            }}
                          />
                        </figure>
                        <p>
                          <b>{dispatcher.name}</b>
                          <span>{dispatcher.phone}</span>
                          <span>{dispatcher.email}</span>
                        </p>
                      </div>
                    </TableCell>

                    {/* Address */}
                    <TableCell>{dispatcher.address}</TableCell>

                    {/* Toggle */}
                    <TableCell>
                      <SwitchToggle 
                        checked={!dispatcher.isBlocked} 
                        onChange={() => {
                          if (dispatcher.id) {
                            handleToggleChange(dispatcher.id, dispatcher.isBlocked, dispatcher.name);
                          }
                        }}
                        disabled={isUpdating || !dispatcher.id}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="table_actions v2">
                        <a href="manage-dispatcher" className="btn btn_primary sm">View Panel</a>
                        <IconButton
                          onClick={() =>
                            navigate("add", { state: { dispatcher: dispatcher.rawData } })
                          }
                        >
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Edit"
                          />
                        </IconButton>

                        <IconButton
                          onClick={() => dispatcher.id && handleDeleteClick(dispatcher.id, dispatcher.name)}
                          disabled={isDeleting || !dispatcher.id}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Pagination
                module={dispatchers}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={dispatcherToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageDispatcher;
